import { motion, useTransform, type MotionValue } from "framer-motion";

import { cn } from "@/lib/utils";

/**
 * Three vocabulary index cards drifting beside a session demo.
 *
 * Shown twice: beside the hero demo on the marketing site, and in the
 * showcase panel on the auth screens. One component for both, because the
 * whole point of the rail is that it *is* the product's vocabulary surface —
 * two copies would drift apart and the landing page would end up
 * advertising something the app stopped doing.
 *
 * The rail is a flex sibling of the demo, not an overlay — so it never sits
 * on top of the thing it is decorating. Below `xl` it disappears entirely:
 * there is no margin to borrow there, and a card clipped by the viewport edge
 * reads as a rendering bug rather than as depth.
 *
 * Drift lives in CSS on an *inner* element on purpose: framer owns
 * `transform` on the outer node for parallax, and two writers on one
 * transform property would fight.
 */

export interface FloatingWord {
  word: string;
  reading: string;
  meaning: string;
  tier: keyof typeof WORD_TIERS;
  position: string;
  tilt: string;
  delay: string;
  duration: string;
  distance: number;
  /** Parallax multiplier — nearer cards travel further. */
  depth: number;
  opacity: number;
}

/** Tier accents for the rail. Same meanings as the vocabulary tier palette,
 *  expressed as the two gradient stops and one chip the card needs. */
export const WORD_TIERS = {
  New: { label: "New", from: "from-stone-400", to: "to-stone-500", chip: "bg-white/10 text-white/60" },
  Learning: { label: "Learning", from: "from-amber-400", to: "to-amber-500", chip: "bg-amber-400/15 text-amber-200" },
  Reviewing: { label: "Reviewing", from: "from-blue-400", to: "to-blue-500", chip: "bg-blue-400/15 text-blue-200" },
  Mastered: { label: "Mastered", from: "from-green-400", to: "to-green-500", chip: "bg-emerald-400/15 text-emerald-200" },
} as const;

export const FLOATING_WORDS: FloatingWord[] = [
  {
    word: "eloquent",
    reading: "ˈeləkwənt",
    meaning: "Fluent and persuasive in speaking",
    tier: "Learning",
    position: "right-6 top-[12%]",
    tilt: "-4deg",
    delay: "0s",
    duration: "9s",
    distance: 16,
    depth: 1.1,
    opacity: 0.95,
  },
  {
    word: "resilient",
    reading: "rɪˈzɪliənt",
    meaning: "Able to recover quickly",
    tier: "Reviewing",
    position: "right-3 top-[40%]",
    tilt: "3deg",
    delay: "-3.4s",
    duration: "11s",
    distance: 21,
    depth: 1.6,
    opacity: 1,
  },
  {
    word: "nuance",
    reading: "ˈnjuːɑːns",
    meaning: "A subtle shade of meaning",
    tier: "Mastered",
    position: "right-7 top-[67%]",
    tilt: "-2deg",
    delay: "-6.2s",
    duration: "10.5s",
    distance: 13,
    depth: 0.65,
    opacity: 0.85,
  },
];

export interface FloatingWordsProps {
  className?: string;
  smoothX: MotionValue<number>;
  smoothY: MotionValue<number>;
  /** Cards to render, in order. Defaults to the full set. */
  words?: FloatingWord[];
}

export function FloatingWords({
  className,
  smoothX,
  smoothY,
  words = FLOATING_WORDS,
}: FloatingWordsProps) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "pointer-events-none relative hidden w-[184px] shrink-0 self-stretch xl:block",
        className,
      )}
    >
      {words.map((card) => (
        <WordCard key={card.word} card={card} smoothX={smoothX} smoothY={smoothY} />
      ))}
    </div>
  );
}

function WordCard({
  card,
  smoothX,
  smoothY,
}: {
  card: FloatingWord;
  smoothX: MotionValue<number>;
  smoothY: MotionValue<number>;
}) {
  const x = useTransform(smoothX, (v) => v * card.depth);
  const y = useTransform(smoothY, (v) => v * card.depth);
  const tier = WORD_TIERS[card.tier];

  const drift = {
    "--loop-tilt": card.tilt,
    "--loop-delay": card.delay,
    // `duration` already carries its unit ("9s"). Appending one here would
    // make the shorthand `9ss`, which is invalid — and an invalid shorthand
    // silently drops the animation name too, so the card would never drift.
    "--loop-duration": card.duration,
    "--loop-distance": `${card.distance}px`,
  } as React.CSSProperties;

  return (
    <motion.div style={{ x, y, opacity: card.opacity }} className={cn("absolute", card.position)}>
      <div className="loop-drift w-[184px]" style={drift}>
        <div className="flex overflow-hidden rounded-2xl border border-white/10 bg-white/[0.06] shadow-2xl shadow-black/40 backdrop-blur-xl">
          {/* Tier stripe — the same left-edge accent the vocabulary list uses. */}
          <div className={cn("w-[3px] shrink-0 bg-gradient-to-b", tier.from, tier.to)} />

          <div className="flex-1 p-3.5">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="font-display text-lg font-semibold leading-tight tracking-tight text-white">
                  {card.word}
                </p>
                <p className="mt-0.5 text-[11px] italic text-white/40">{card.reading}</p>
              </div>
              <span className={cn("mt-0.5 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider", tier.chip)}>
                {tier.label}
              </span>
            </div>
            <p className="mt-2.5 text-[13px] leading-snug text-white/60">{card.meaning}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
}